// SPDX-License-Identifier: Elastic-2.0
/**
 * MediaControls — track info + playback buttons + volume slider.
 *
 * Per task spec § 4:
 *   - Track info: title + artist (+ album art when `state.album_art` is
 *     a URL). Fall through gracefully when missing.
 *   - Playback row: prev / play-or-pause / next — dispatches
 *     `media_previous`, `media_play`/`media_pause`, `media_next`.
 *   - Volume Slider 0-100 → `set_volume` with `{ volume_pct }`.
 *
 * Icons reuse the existing set (`arrow_right`, `close`) — these are the
 * shapes the dashboard card already uses for play/pause to avoid adding
 * new glyphs in this slice.
 */
import { useTranslation } from "react-i18next";

import { Icon } from "@/components/Icon/Icon";
import { Slider } from "@/components/Slider";

import type { Device } from "@/api/gateway";

import styles from "../DeviceModal.module.css";

export interface MediaControlsProps {
  device: Device;
  onAction: (
    action: string,
    params: Record<string, unknown>,
    optimisticStatePatch?: Record<string, unknown>,
  ) => void;
  disabled?: boolean;
}

function isPlaying(device: Device): boolean {
  const state = device.state ?? {};
  const playback = state.playback ?? state.state;
  if (typeof playback === "string") return playback.toLowerCase() === "playing";
  return Boolean(playback);
}

export function MediaControls({
  device,
  onAction,
  disabled,
}: MediaControlsProps): JSX.Element {
  const { t } = useTranslation();
  const state = device.state ?? {};
  const playing = isPlaying(device);
  const title =
    typeof state.title === "string"
      ? state.title
      : typeof state.media_title === "string"
        ? state.media_title
        : null;
  const artist =
    typeof state.artist === "string"
      ? state.artist
      : typeof state.media_artist === "string"
        ? state.media_artist
        : null;
  const albumArt =
    typeof state.album_art === "string" && state.album_art.length > 0
      ? state.album_art
      : null;
  const volume = typeof state.volume === "number" ? state.volume : 50;

  function handlePlayPause(): void {
    onAction(
      playing ? "media_pause" : "media_play",
      {},
      { playback: playing ? "paused" : "playing" },
    );
  }

  function handleNext(): void {
    onAction("media_next", {});
  }

  function handlePrev(): void {
    onAction("media_previous", {});
  }

  function handleVolume(value: number): void {
    onAction("set_volume", { volume_pct: value }, { volume: value });
  }

  return (
    <div className={styles.controlsStack} data-testid="media-controls">
      <div className={styles.mediaTrack}>
        {albumArt ? (
          <img
            src={albumArt}
            alt=""
            className={styles.mediaArt}
            data-testid="media-album-art"
          />
        ) : null}
        <div className={styles.mediaText}>
          <span className={styles.mediaTitle}>
            {title ?? t("device_modal.controls.track")}
          </span>
          {artist ? <span className={styles.mediaArtist}>{artist}</span> : null}
        </div>
      </div>
      <div className={styles.mediaButtons} role="group" aria-label="Playback controls">
        <button
          type="button"
          className={styles.mediaButton}
          onClick={handlePrev}
          disabled={disabled}
          aria-label="Previous"
          data-testid="media-prev"
        >
          <Icon name="arrow_right" size={20} style={{ transform: "rotate(180deg)" }} />
        </button>
        <button
          type="button"
          className={styles.mediaButtonPrimary}
          onClick={handlePlayPause}
          disabled={disabled}
          aria-label={playing ? "Pause" : "Play"}
          data-testid="media-play-pause"
        >
          <Icon name={playing ? "close" : "arrow_right"} size={22} />
        </button>
        <button
          type="button"
          className={styles.mediaButton}
          onClick={handleNext}
          disabled={disabled}
          aria-label="Next"
          data-testid="media-next"
        >
          <Icon name="arrow_right" size={20} />
        </button>
      </div>
      <Slider
        value={volume}
        onChange={handleVolume}
        min={0}
        max={100}
        label={t("device_modal.controls.volume")}
        formatValue={(v) => `${v}%`}
        disabled={disabled}
      />
    </div>
  );
}

export default MediaControls;
